import { configureApiClient, type PrincipalResponse } from '../api';

/**
 * The signed-in session. It lives in memory and nowhere else.
 *
 * There is no `localStorage`, no `sessionStorage` and no cookie written by this
 * application, and the lint rules take those APIs away so it stays that way.
 *
 * That matters more here than it would in most products. The principal carries
 * a person's name, and the screens built on top of this shell carry CPFs and
 * dates of birth of insured people. Anything written to browser storage
 * survives the tab, is readable by any script that manages to run on the page,
 * and sits on disk on whatever machine it was typed on -- a shared HR
 * workstation, more often than not.
 *
 * It used to cost a reload: nothing survived one, so the person signed in
 * again. It no longer does, and nothing above was given up to arrange that.
 * What survives a reload is a *refresh* token in a cookie this application
 * cannot read, write or see -- `httpOnly`, `Secure`, `SameSite=Strict`, and
 * scoped to the renewal endpoints, so it is never attached to a request that
 * carries roster data. Booting the application spends it for a fresh access
 * token, which lands back here, in memory, exactly as signing in does. See
 * `restore.ts` for that exchange and `app/auth/refresh.py` for the other half.
 *
 * The shared-workstation reasoning is intact because the cookie has no
 * `Max-Age`: the browser drops it when it closes. Reloading the tab survives;
 * walking away and closing the browser still ends the session. That distinction
 * is the whole feature, and it is the one thing to preserve if this file
 * changes again.
 */

export interface Session {
  /** Presented as `Authorization: Bearer <token>`. Never rendered, never stored. */
  readonly accessToken: string;
  readonly expiresAt: Date;
  /** Who the *server* says we are. Not decoded from the token. */
  readonly principal: PrincipalResponse;
}

/** Why there is no session, when there was one a moment ago. */
export type SessionEndReason =
  /** The person asked to leave. */
  | 'signed_out'
  /** The token ran out, or the server stopped accepting it. */
  | 'expired';

export interface SessionState {
  readonly session: Session | null;
  /** Null on first load; set once a session has ended, so login can explain why. */
  readonly endedReason: SessionEndReason | null;
}

const SIGNED_OUT: SessionState = { session: null, endedReason: null };

let state: SessionState = SIGNED_OUT;
const listeners = new Set<() => void>();
let expiryTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Expire a little early. A token that the client still believes in but the
 * server has already rejected produces a request that fails for no visible
 * reason; expiring first turns that into an ordinary trip to the login screen.
 */
const EXPIRY_MARGIN_MS = 30_000;

function publish(next: SessionState): void {
  state = next;
  for (const listener of listeners) {
    listener();
  }
}

function cancelExpiryTimer(): void {
  if (expiryTimer !== undefined) {
    clearTimeout(expiryTimer);
    expiryTimer = undefined;
  }
}

function scheduleExpiry(expiresAt: Date): void {
  cancelExpiryTimer();
  const delay = expiresAt.getTime() - Date.now() - EXPIRY_MARGIN_MS;
  if (delay <= 0) {
    endSession('expired');
    return;
  }
  expiryTimer = setTimeout(() => endSession('expired'), delay);
}

export function getSessionState(): SessionState {
  return state;
}

export function getSession(): Session | null {
  return state.session;
}

export function getAccessToken(): string | undefined {
  return state.session?.accessToken;
}

export function subscribeToSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function startSession(session: Session): void {
  publish({ session, endedReason: null });
  scheduleExpiry(session.expiresAt);
}

export function endSession(reason: SessionEndReason): void {
  cancelExpiryTimer();
  if (state.session === null) {
    // Nothing to end, so nothing to explain. This is the state a first-time
    // visitor is in, and the silent renewal attempt on boot fails for them with
    // a 401 like any other -- which the transport reports here. Recording that
    // as "your session ended" would greet somebody who has never been here with
    // a message about a session they never had.
    return;
  }
  publish({ session: null, endedReason: reason });
}

/** Back to a clean slate, with no "your session ended" message. For tests. */
export function resetSession(): void {
  cancelExpiryTimer();
  publish(SIGNED_OUT);
}

/**
 * Hand the transport what it needs, in one direction.
 *
 * The API layer knows nothing about sessions; it asks for a token when it needs
 * one and reports back when the server refuses it. Registering at module load
 * means a request issued before any component has mounted still carries the
 * right credentials.
 */
export function connectSessionToApiClient(): void {
  configureApiClient({
    getAccessToken,
    onUnauthenticated: () => endSession('expired'),
  });
}

connectSessionToApiClient();
